import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import ReactTooltip from 'react-tooltip'
import { endOfDay, addHours, startOfHour } from 'date-fns'

import Icon from '../../../Icons'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../../FormLegacy/FormFieldsV2/TextAreaField'
import ToggleField from '../../../FormLegacy/FormFieldsV2/ToggleField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import Filter from '../../../Filter/Filter'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import API from '../../../../api/api'

import {
  getBuildingsOptions,
  getGroupsOptions,
  getTagsOptions,
  renderFieldPlaceholder,
  validationSchema,
  combineDateTime,
  getInitialScheduledDate,
  getInitialScheduledTime,
  getMinTimeForSchedule,
} from './helpers'
import { ATTACHMENT_SIZE_LIMIT, ERROR_MESSAGE_TIMEOUT, MAX_ATTACHMENTS } from './constants'

const PostForm = ({
  buildings,
  createPost,
  updatePost,
  editPostData,
  handleCancel,
  onSuccess,
  userRole,
  innerGroups,
  buildingId,
  buildingTimezone = 'America/Chicago',
}) => {
  const ref = useRef(null)
  const timeout = useRef(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [images, setImages] = useState([])
  const [isMultipleBuildingSelection, setIsMultipleBuildingSelection] = useState(false)
  const [groupsOptions, setGroupsOptions] = useState([])
  const { displaySnackbar } = useSnackbar()
  const isPublished = editPostData?.published

  const buildingsOptions = getBuildingsOptions(buildings, isMultipleBuildingSelection)

  const tagsOptions = getTagsOptions(buildings)

  const formattedGroupsOptions = getGroupsOptions(isMultipleBuildingSelection, groupsOptions)

  const handleFormSubmit = async (values, { setSubmitting }) => {
    const { title, message, tags, buildings, groups, isScheduled, scheduledDate, scheduledTime } =
      values
    const groupsIds = groups.map((group) => group.id)
    const sendData = {
      title,
      message,
    }

    if (values.attachments.length > 0) {
      for (let i = 0; i <= values.attachments.length; i += 1) {
        if (values.attachments[i] !== undefined) {
          sendData[`image${i + 1}`] =
            (values.attachments[i] && values.attachments[i].file) || values.attachments[i]
        }
      }
    }

    if (tags.length) {
      sendData.buildingTags = tags.map((tag) => tag.text)
    } else {
      sendData.buildings = buildings.map((building) => building.id)
    }

    if (isMultipleBuildingSelection) {
      sendData.systemGroupNames = groups.map((group) => group.text)
    } else {
      sendData.groups = groupsIds
    }

    if (isScheduled && scheduledDate && scheduledTime) {
      const selectedBuildingTimezone = buildings[0]?.timezone || buildingTimezone
      sendData.scheduledAt = combineDateTime(scheduledDate, scheduledTime, selectedBuildingTimezone)
    } else {
      sendData.scheduledAt = null
    }

    setSubmitting(true)

    try {
      editPostData
        ? await updatePost(editPostData.groupId, editPostData.id, sendData)
        : await createPost(groupsIds, sendData)
      handleCancel()
      const successMessage = isScheduled
        ? 'Post has been scheduled'
        : editPostData
          ? 'Post has been updated'
          : 'Post has been created'
      displaySnackbar(successMessage)
      onSuccess && onSuccess()
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setSubmitting(false)
    }
  }

  const getFreshGroups = async (buildingId) => {
    const { data: freshGroups } = await API.getGroups(buildingId)
    if (freshGroups.length) {
      setGroupsOptions(
        freshGroups.map((group) => {
          return { id: group.id, text: group.name }
        }),
      )
    }
  }

  const getFreshGroupsFromBuildings = async (selectedBuildings) => {
    const allGroups = (
      await Promise.all(
        selectedBuildings.map(async (building) => {
          const { data: freshGroups } = await API.getGroups(building.id)
          return freshGroups.map((group) => ({ id: group.id, text: group.name }))
        }),
      )
    ).flat()

    // Remove objects that are not duplicated by text
    const counts = {}
    const duplicatedNames = new Set(
      allGroups
        .filter((obj) => {
          counts[obj.text] = (counts[obj.text] || 0) + 1
          return counts[obj.text] > 1
        })
        .map((obj) => obj.text),
    )

    const filteredArray = allGroups.filter((obj) => duplicatedNames.has(obj.text))

    // Remove duplicates by text
    const uniqueArray = Array.from(new Set(filteredArray.map((obj) => obj.text))).map((text) =>
      filteredArray.find((obj) => obj.text === text),
    )

    if (uniqueArray.length) {
      setGroupsOptions(uniqueArray)
    }
  }

  const onChangeInnerBuilding = async (selectedBuildingOrArray, isMulti) => {
    if (!selectedBuildingOrArray) {
      setGroupsOptions([])
    } else if (isMulti) {
      setGroupsOptions([])
      getFreshGroupsFromBuildings(selectedBuildingOrArray)
    } else {
      getFreshGroups(selectedBuildingOrArray.id)
    }
  }

  const showErrorMessage = (message) => {
    setErrorMessage(message)
    timeout.current = setTimeout(() => {
      setErrorMessage(null)
    }, ERROR_MESSAGE_TIMEOUT)
  }

  useEffect(() => {
    if (editPostData && buildingId) {
      getFreshGroups(buildingId)
    }

    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  return (
    <div className="new-post-form">
      <Formik
        key={editPostData ? `edit-${groupsOptions.length > 0}` : 'new'}
        enableReinitialize={true}
        initialValues={{
          title: editPostData ? editPostData.title : '',
          message: editPostData ? editPostData.message : '',
          attachments: editPostData ? editPostData.images : [],
          tags: [],
          buildings: [],
          groups: editPostData
            ? groupsOptions.filter(
                (group) => (editPostData.groupId?.id ?? editPostData.groupId) === group.id,
              )
            : [],
          isScheduled: editPostData ? !!editPostData.scheduledAt : false,
          isPublished: editPostData ? !!editPostData.published : false,
          scheduledDate: getInitialScheduledDate(editPostData?.scheduledAt, buildingTimezone),
          scheduledTime: getInitialScheduledTime(editPostData?.scheduledAt, buildingTimezone),
        }}
        validationSchema={validationSchema(buildingTimezone)}
        onSubmit={handleFormSubmit}
      >
        {({
          handleChange,
          setFieldValue,
          values,
          errors,
          touched,
          setFieldTouched,
          isValid,
          dirty,
          isSubmitting,
          handleSubmit,
        }) => {
          const selectedTagsLabels = values.tags.map((tag) => tag.text)
          const selectedBuildingsLabels = values.buildings.map((building) => building.text)
          const selectedGroupsLabels = values.groups.map((group) => group.text)

          const handleBuildingsSelect = (e) => {
            if (isMultipleBuildingSelection) {
              const selectedBuildings = e.target.value
              innerGroups && onChangeInnerBuilding(selectedBuildings, true)
              handleChange(e)
              return
            }
            const selectedBuilding = e.target.value
            innerGroups && onChangeInnerBuilding(selectedBuilding)
            setFieldValue('buildings', [selectedBuilding])
            setFieldValue('groups', [])
          }

          const handleEnableMultipleBuildingsSelection = (e) => {
            const isChecked = e.target.checked
            setIsMultipleBuildingSelection(isChecked)
            setFieldValue('buildings', [])
            setFieldValue('tags', [])
            setFieldValue('groups', [])

            if (isChecked) {
              setFieldValue('isScheduled', false)
              setFieldValue('scheduledDate', null)
              setFieldValue('scheduledTime', null)
            }
            innerGroups && onChangeInnerBuilding(null)
          }

          const handlePickerAttachment = (e) => {
            const hasExtension = (inputName, exts) => {
              const fileName = document.querySelector(`input[name="${inputName}"]`).value
              return new RegExp('(' + exts.join('|').replace(/\./g, '\\.') + ')$').test(fileName)
            }
            const file = e.target.files[0]
            if (values.attachments.length >= MAX_ATTACHMENTS) {
              showErrorMessage('You can upload up to 5 files.')
            } else if (file.size > ATTACHMENT_SIZE_LIMIT) {
              showErrorMessage('File size should be less than 10MB.')
            } else if (!hasExtension('files', ['.jpg', '.jpeg', '.png', '.JPG', '.JPEG', '.PNG'])) {
              showErrorMessage('Please select correct file.')
            } else {
              const reader = new FileReader()
              let image = ''
              reader.onload = (e) => {
                image = e.target.result
                setImages([...images, { name: file.name, image }])
                setFieldValue('attachments', [...values.attachments, file])
              }
              reader.readAsDataURL(file)
            }
          }
          const handleScheduleToggle = (e) => {
            const isChecked = e.target.checked
            setFieldValue('isScheduled', isChecked)
            if (isChecked && !values.scheduledDate) {
              setFieldValue('scheduledDate', new Date())
              setFieldValue('scheduledTime', startOfHour(addHours(new Date(), 1)))
            }
          }

          const removeFile = (id) => {
            setFieldValue(
              'attachments',
              values.attachments.filter((file, i) => i !== id),
            )
            if (images.length) setImages(images.filter((img, i) => i !== id))
          }

          const isScheduleValid =
            !values.isScheduled || (values.scheduledDate && values.scheduledTime)
          const isDisabled = !isValid || !dirty || !values.groups.length || !isScheduleValid

          return (
            <div
              className={`new-post-form__container ${isSubmitting ? 'is-disabled' : ''}`}
              ref={ref}
            >
              <div className="window-header">
                <div className="left-side">
                  <div className="title">{editPostData ? 'Edit post' : 'New post'}</div>
                </div>
                <div className="right-side">
                  <div className="navi-button" onClick={handleCancel}>
                    <Icon icon="cross" />
                  </div>
                </div>
              </div>
              <div className="main-view">
                {!editPostData ? (
                  <React.Fragment>
                    <div className="field-wrapper">
                      <CheckboxField
                        name="Enable Multiple Building Selection"
                        label="Enable Multiple Building Selection"
                        checked={isMultipleBuildingSelection}
                        onChange={handleEnableMultipleBuildingsSelection}
                      />
                    </div>
                    <div className="field-wrapper">
                      <Filter
                        name="buildings"
                        placeholder={renderFieldPlaceholder(selectedBuildingsLabels, 'buildings')}
                        additionalClass="select-tags-or-buildings-dropdown"
                        options={buildingsOptions}
                        value={values.buildings}
                        onChange={handleBuildingsSelect}
                        config={{
                          multiple: isMultipleBuildingSelection,
                          hasSearch: true,
                        }}
                      />
                    </div>
                  </React.Fragment>
                ) : null}
                <div className="field-wrapper-row">
                  <div className="field-wrapper">
                    <Filter
                      name="groups"
                      placeholder={renderFieldPlaceholder(selectedGroupsLabels, 'groups')}
                      additionalClass="select-tags-or-buildings-dropdown"
                      options={formattedGroupsOptions}
                      value={values.groups}
                      onChange={handleChange}
                      config={{
                        multiple: true,
                        hasSearch: true,
                      }}
                      disabled={!!editPostData}
                    />
                  </div>
                  {!editPostData &&
                  isMultipleBuildingSelection &&
                  userRole !== 'PropertyManager' ? (
                    <div className="field-wrapper">
                      <Filter
                        name="tags"
                        placeholder={renderFieldPlaceholder(selectedTagsLabels, 'tags')}
                        additionalClass="select-tags-or-buildings-dropdown"
                        options={tagsOptions}
                        value={values.tags}
                        onChange={handleChange}
                        config={{
                          multiple: true,
                          hasSearch: true,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="field-wrapper">
                  <TextField
                    name={'title'}
                    placeholder={'Title'}
                    value={values.title}
                    isError={touched.title && !!errors.title}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('title')}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                    maxLength="50"
                  />
                </div>
                <div className="field-wrapper">
                  <TextAreaField
                    name={'message'}
                    placeholder={'What do you want to say?'}
                    onChange={handleChange}
                    value={values.message}
                    isError={touched.message && !!errors.message}
                    onBlur={() => setFieldTouched('message')}
                    disabled={isSubmitting}
                    maxLength="1000"
                  />
                </div>
                {values.attachments.length ? (
                  <div className="field-wrapper" style={{ border: 'none', paddingBottom: '0' }}>
                    <div className="files-container">
                      {values.attachments.map((file, i) => {
                        return (
                          <div key={i} className="file-container">
                            <div className="attachment-data">
                              <div
                                className="file-name-image-icon"
                                style={{
                                  backgroundImage: `url(${
                                    images.length && images.some((img) => img.name === file.name)
                                      ? images.find((img) => img.name === file.name).image
                                      : getAssetLink({
                                          path: 'chitown-images/groups',
                                          file: file.name || file,
                                        })
                                  })`,
                                }}
                              />
                              <span className="file-name-label">
                                {file.name || file.fileName || file}
                              </span>
                            </div>
                            <div className="remove-icon" onClick={() => removeFile(i)}>
                              <Icon icon="cross" />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : null}
                <div
                  className="field-wrapper"
                  style={
                    values.attachments.length < MAX_ATTACHMENTS
                      ? { border: 'none' }
                      : { visibility: 'hidden' }
                  }
                >
                  <label className="icon-container" htmlFor="file-input">
                    <div data-tip data-for="max-size-tip">
                      <Icon icon="picture" />
                      <ReactTooltip className="tooltip" id="max-size-tip" multiline place="top">
                        The maximum allowed size for the attachment file is 10 MB.
                      </ReactTooltip>
                    </div>
                  </label>
                  <input
                    id="file-input"
                    type="file"
                    name="files"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={(e) => handlePickerAttachment(e)}
                    onClick={(e) => (e.target.value = null)}
                  />
                </div>
                {!isMultipleBuildingSelection && !isPublished && (
                  <div className="field-wrapper schedule-toggle">
                    <ToggleField
                      name="isScheduled"
                      label="Schedule the post"
                      value={values.isScheduled}
                      onChange={handleScheduleToggle}
                      labelOnRight
                    />
                  </div>
                )}
                {values.isScheduled && !isMultipleBuildingSelection && !isPublished && (
                  <div className="field-wrapper-row schedule-fields">
                    <div className="field-wrapper schedule-date">
                      <DateField
                        name="scheduledDate"
                        label="Delivery Date"
                        placeholder="Select Delivery Date"
                        value={values.scheduledDate}
                        onChange={(e) => setFieldValue('scheduledDate', e.target.value)}
                        minDate={new Date()}
                        popperPlacement="top-start"
                      />
                    </div>
                    <div className="field-wrapper schedule-time">
                      <DateField
                        name="scheduledTime"
                        label="Delivery Time"
                        value={values.scheduledTime}
                        onChange={(e) => setFieldValue('scheduledTime', e.target.value)}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={15}
                        dateFormat="h:mm aa"
                        minTime={getMinTimeForSchedule(
                          values.scheduledDate,
                          values.buildings[0]?.timezone || buildingTimezone,
                        )}
                        maxTime={endOfDay(new Date())}
                        popperPlacement="top-start"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="window-footer">
                <div className="error-message">{errorMessage}</div>
                <div className="footer-buttons">
                  <button
                    type="submit"
                    className={`button button-black is-block ${isSubmitting ? 'is-loading' : ''}`}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    {'Cancel'}
                  </button>
                  <button
                    type="submit"
                    className={`button button-primary is-block ${isSubmitting ? 'is-loading' : ''}`}
                    onClick={handleSubmit}
                    disabled={isSubmitting || isDisabled}
                  >
                    {isPublished
                      ? 'Save Changes'
                      : values.isScheduled
                        ? 'Schedule Post'
                        : 'Create Post'}
                  </button>
                </div>
              </div>
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

PostForm.propTypes = {
  buildings: PropTypes.array,
  createPost: PropTypes.func,
  updatePost: PropTypes.func,
  editPostData: PropTypes.object,
  handleCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  userRole: PropTypes.string,
  innerGroups: PropTypes.bool,
  buildingId: PropTypes.number,
  buildingTimezone: PropTypes.string,
}

export default PostForm
